import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Bell, Check, X } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  recordSoftPromptDeclined,
  recordSoftPromptShown,
} from '@/hooks/usePushSoftPrompt';
import { useHaptics } from '@/hooks/useHaptics';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where the sheet was triggered from — for telemetry. */
  trigger:
    | 'first_workout'
    | 'return_visit_non_starter'
    | 'nth_workout'
    | 'dashboard_post_onboarding'
    | 'streak_started'
    | 'first_food_log';
  /** Surface the sheet is mounted on — for funnel joins. */
  surface?: 'dashboard' | 'workout_summary' | 'streak' | 'nutrition';
  /** Optional per-mount title override. The benefits stay the same everywhere. */
  headline?: string;
}

const BENEFITS = [
  'A reminder on your training days',
  'A heads-up before your streak breaks',
  'Your weekly progress, the moment it lands',
];

/**
 * Value-prop sheet that precedes the native OS push permission prompt.
 * The OS prompt (iOS) can only fire ONCE per install, so we frame the
 * ask in-app first. "Turn on notifications" hands off to the OS prompt.
 * "Not now" records a soft-decline and closes — the OS prompt is NOT
 * fired, so we can re-ask at a later high-value moment.
 */
export function PushValuePropSheet({
  open,
  onClose,
  trigger,
  surface,
  headline = 'Stay on track between sessions',
}: Props) {
  const { requestPermission } = usePushNotifications();
  const posthog = usePostHog();
  const haptics = useHaptics();
  const shownRef = useRef(false);

  useEffect(() => {
    if (!open || shownRef.current) return;
    shownRef.current = true;
    recordSoftPromptShown();
    posthog?.capture('push_soft_prompt_shown', { trigger, surface });
  }, [open, trigger, surface, posthog]);

  if (!open) return null;

  const handleAccept = async () => {
    haptics.lightTap();
    posthog?.capture('push_soft_prompt_accepted', { trigger, surface });
    onClose();
    // Fire the OS prompt AFTER closing the sheet so the two UIs don't stack.
    // requestPermission marks the OS-prompted flag internally on completion.
    try {
      const granted = await requestPermission();
      posthog?.capture('push_os_prompt_result', { trigger, surface, granted });
    } catch {
      /* non-fatal */
    }
  };

  const handleDecline = () => {
    haptics.lightTap();
    recordSoftPromptDeclined();
    posthog?.capture('push_soft_prompt_declined', { trigger, surface });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center px-safe"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={handleDecline}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md p-6 pb-safe-nav"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--hos-card)',
          borderTop: '1px solid var(--hos-border)',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
        }}
      >
        <div className="flex justify-center -mt-3 mb-1">
          <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--hos-border-lit)' }} />
        </div>
        <div className="flex justify-end -mt-2 -mr-2">
          <button
            type="button"
            onClick={handleDecline}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center active:scale-95 transition-transform"
            style={{ color: 'var(--hos-text-2)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          className="w-14 h-14 flex items-center justify-center mb-4"
          style={{
            background: 'var(--hos-green-soft)',
            border: '1px solid var(--hos-green-border)',
            borderRadius: 20,
          }}
        >
          <Bell className="w-7 h-7" style={{ color: 'var(--hos-green)' }} />
        </div>
        <h2
          className="font-black"
          style={{ color: 'var(--hos-text)', fontSize: 22, letterSpacing: '-0.3px' }}
        >
          {headline}
        </h2>

        <div className="mt-4 space-y-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-center gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'var(--hos-green-soft)' }}
              >
                <Check className="w-3.5 h-3.5" style={{ color: 'var(--hos-green)' }} strokeWidth={3} />
              </div>
              <span className="text-sm font-medium" style={{ color: 'var(--hos-text)' }}>
                {b}
              </span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs" style={{ color: 'var(--hos-text-3)' }}>
          Only when it matters, no spam, ever.
        </p>

        <button
          type="button"
          onClick={handleAccept}
          className="mt-6 w-full h-12 rounded-2xl font-bold text-sm active:scale-[0.98] transition-transform"
          style={{ background: 'var(--hos-green)', color: 'var(--hos-green-on-fill)' }}
        >
          Turn on notifications
        </button>
        <button
          type="button"
          onClick={handleDecline}
          className="mt-2 w-full h-11 rounded-2xl font-semibold text-sm active:scale-[0.98] transition-transform"
          style={{ color: 'var(--hos-text-2)' }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
